import Dexie, { EntityTable } from 'dexie';

import {
  CommonUserFieldsFragment,
  ConstructionSiteFieldsFragment,
} from 'generated/graphql';

interface SegmentDrawConfig {
  id: string;
  label: string;
  order: number;
  shape: string;
}

interface TunnelPosition {
  keyPosition: number;
  positionX: number;
}

interface DefectReportType {
  id: string;
  label: string;
  color: string;
}

interface DefectIndicator {
  id: string;
  label: string;
}

interface Ring {
  id: string;
  ringNumber: number;
  keyPosition: number;
  userFirstName: string | null;
  userLastName: string | null;
  compliantDate: string | null;
  desynchronizedAt: string | null;
}

interface Segment {
  id: string;
  ringId: string;
  segmentType: string;
  serialNumber: string;
}

export interface StrokePoint {
  x: number;
  y: number;
}

interface DefectReport {
  id: string;
  ringId: string;
  segmentType: string;
  serialNumber: string | null;
  defectReportTypeId: string;
  defectIndicatorId: string | null;
  authorId: string;
  author: string;
  description: string | null;
  toRepair: boolean;
  strokes: StrokePoint[];
  createdAt: string;
  submittedAt: string | null;
  desynchronizedAt: string | null;
  deletedAt: string | null;
}

interface DefectReportPicture {
  id: string;
  defectReportId: string;
  data: string;
  desynchronizedAt: string | null;
  deletedAt: string | null;
}

interface RepairReport {
  id: string;
  defectReportId: string;
  author: string;
  description: string | null;
  createdAt: string;
  submittedAt: string | null;
  desynchronizedAt: string | null;
  deletedAt: string | null;
}

interface UserSessionRecord {
  id: 'singleton';
  user: CommonUserFieldsFragment;
}

interface ConstructionSiteListRecord {
  id: 'singleton';
  sites: ConstructionSiteFieldsFragment[];
}

interface RepairReportPicture {
  id: string;
  repairReportId: string;
  data: string;
  desynchronizedAt: string | null;
  deletedAt: string | null;
}

const db = new Dexie('cap') as Dexie & {
  segmentDrawConfigs: EntityTable<SegmentDrawConfig, 'id'>;
  tunnelPositions: EntityTable<TunnelPosition, 'keyPosition'>;
  defectReportTypes: EntityTable<DefectReportType, 'id'>;
  defectIndicators: EntityTable<DefectIndicator, 'id'>;
  rings: EntityTable<Ring, 'id'>;
  segments: EntityTable<Segment, 'id'>;
  defectReports: EntityTable<DefectReport, 'id'>;
  defectReportPictures: EntityTable<DefectReportPicture, 'id'>;
  repairReports: EntityTable<RepairReport, 'id'>;
  repairReportPictures: EntityTable<RepairReportPicture, 'id'>;
  userSession: EntityTable<UserSessionRecord, 'id'>;
  constructionSiteList: EntityTable<ConstructionSiteListRecord, 'id'>;
};

db.version(1).stores({
  segmentDrawConfigs: 'id, label, order, shape',
  tunnelPositions: 'keyPosition',
  defectReportTypes: 'id',
  rings:
    'id, ringNumber, keyPosition, userFirstName, userLastName, compliantDate, desynchronizedAt',
  segments: 'id, ringId, segmentType',
  defectReports: 'id, ringId, defectReportTypeId, desynchronizedAt',
  repairReports: 'defectReportId, id, desynchronizedAt',
});

db.version(2)
  .stores({ userSession: 'id', constructionSiteList: 'id' })
  .upgrade((tx) => {
    tx.table('defectReports').toCollection().modify({ deletedAt: null });
    tx.table('repairReports').toCollection().modify({ deletedAt: null });
  });

db.version(3).stores({
  defectReportPictures: 'id, defectReportId, desynchronizedAt',
  repairReportPictures: 'id, repairReportId, desynchronizedAt',
});

db.version(4)
  .stores({ repairReports: null })
  .upgrade(() => {});

db.version(5).stores({
  repairReports: 'id, defectReportId, desynchronizedAt',
});

db.version(6)
  .stores({})
  .upgrade((tx) => {
    tx.table('defectReports').toCollection().modify({ serialNumber: null });
  });

db.version(7)
  .stores({})
  .upgrade((tx) => {
    tx.table('defectReportPictures').toCollection().modify({ deletedAt: null });
    tx.table('repairReportPictures').toCollection().modify({ deletedAt: null });
  });

db.version(8)
  .stores({ defectIndicators: 'id' })
  .upgrade((tx) => {
    tx.table('defectReports')
      .toCollection()
      .modify({ defectIndicatorId: null });
  });

export {
  type SegmentDrawConfig,
  type TunnelPosition,
  type DefectReportType,
  type DefectIndicator,
  type Ring,
  type Segment,
  type DefectReport,
  type DefectReportPicture,
  type RepairReport,
  type RepairReportPicture,
  type UserSessionRecord,
  type ConstructionSiteListRecord,
  db,
};
